import React from 'react';
import { Server, Globe, ChevronRight, Cpu, MapPin, Activity } from 'lucide-react';
import type { XCloudServer } from '@/types/xcloud';

interface ServerCardProps {
  server: XCloudServer;
  onClick: () => void;
  view?: 'grid' | 'list';
}

function getServerStatusLabel(status: string): string {
  if (status === 'active' || status === 'provisioned') return 'Connected';
  if (status === 'provisioning') return 'Provisioning';
  if (status === 'inactive') return 'Inactive';
  if (status === 'failed') return 'Failed';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function getServerStatusClass(status: string): string {
  if (status === 'active' || status === 'provisioned') return 'text-emerald-500';
  if (status === 'provisioning') return 'text-sky-500';
  if (status === 'inactive') return 'text-muted-foreground';
  return 'text-red-500';
}

function StatusDot({ status }: { status: string }) {
  const colorMap: Record<string, string> = {
    active: 'bg-emerald-500',
    provisioned: 'bg-emerald-500',
    inactive: 'bg-muted-foreground',
    provisioning: 'bg-sky-500 animate-pulse',
    failed: 'bg-red-500',
  };
  return (
    <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${colorMap[status] ?? 'bg-muted-foreground'}`} />
  );
}

export function ServerCard({ server, onClick, view = 'grid' }: ServerCardProps) {
  const locationLabel = server.location || server.region;
  const providerLabel = server.provider
    ? server.provider.charAt(0).toUpperCase() + server.provider.slice(1).replace(/-/g, ' ')
    : 'Other Provider';
  const locationDisplay = locationLabel
    ? locationLabel.charAt(0).toUpperCase() + locationLabel.slice(1).replace(/-/g, ' ')
    : 'Unknown';

  if (view === 'list') {
    return (
      <button
        onClick={onClick}
        className="w-full text-left group flex items-center gap-3 px-4 py-3 rounded-xl border border-border bg-white dark:bg-card hover:border-sky-500/40 hover:bg-muted/30 transition-all"
      >
        <div className="w-8 h-8 rounded-lg bg-sky-500/10 flex items-center justify-center shrink-0">
          <Server className="w-4 h-4 text-sky-500" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm text-foreground truncate">{server.name}</span>
            <span className={`flex items-center gap-1 text-xs font-medium ${getServerStatusClass(server.status)}`}>
              <StatusDot status={server.status} />
              {getServerStatusLabel(server.status)}
            </span>
          </div>
          <div className="flex items-center gap-3 mt-0.5">
            {server.ip_address && (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <Globe className="w-3 h-3 shrink-0" />
                {server.ip_address}
              </span>
            )}
            <span className="text-xs text-muted-foreground">{providerLabel}</span>
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <MapPin className="w-3 h-3 shrink-0" />
              {locationDisplay}
            </span>
          </div>
        </div>

        <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors shrink-0" />
      </button>
    );
  }

  return (
    <button
      onClick={onClick}
      className="w-full text-left group rounded-xl border border-border bg-white dark:bg-card hover:border-sky-500/40 hover:shadow-sm transition-all p-5"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-sky-500/10 flex items-center justify-center shrink-0">
            <Server className="w-5 h-5 text-sky-500" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
              <span className="font-semibold text-sm text-foreground truncate">{server.name}</span>
              <span className={`flex items-center gap-1 text-xs font-medium ${getServerStatusClass(server.status)}`}>
                <StatusDot status={server.status} />
                {getServerStatusLabel(server.status)}
              </span>
            </div>
            {server.ip_address && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Globe className="w-3 h-3 shrink-0" />
                <span className="truncate">{server.ip_address}</span>
              </div>
            )}
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors shrink-0 mt-1" />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Cpu className="w-3.5 h-3.5 text-sky-500/70 shrink-0" />
          <span className="truncate">{providerLabel}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <MapPin className="w-3.5 h-3.5 text-sky-500/70 shrink-0" />
          <span className="truncate">{locationDisplay}</span>
        </div>
        {server.php_version && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="text-sky-500/70 font-semibold text-[10px] shrink-0">PHP</span>
            <span className="truncate">{server.php_version}</span>
          </div>
        )}
        {server.stack && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Activity className="w-3.5 h-3.5 text-sky-500/70 shrink-0" />
            <span className="truncate uppercase">{server.stack}</span>
          </div>
        )}
      </div>
    </button>
  );
}
